import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FaEdit, FaFileMedical, FaCheckCircle, FaFileArchive, FaUpload, FaTrash, FaFilePdf, FaBan, FaUndo } from 'react-icons/fa';
import Swal from 'sweetalert2';
import { normalizarRut } from '../../utils/rutUtils';
import {
    TIPOS_LICENCIA,
    JORNADAS_REPOSO,
    OPERADORES,
    TIPOS_PROFESIONAL,
    MOTIVOS_NO_RECEPCION,
    buscar,
    reglasDelTipo,
    fechaTexto,
    validarLicencia,
    validarNoRecepcion
} from '../../utils/licenciasMedicas';
import '../../styles/pages/RRHH/LicenciasMedicasPage.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Mismos nombres que las columnas de rrhh_licencias_medicas.
const FORMULARIO_VACIO = {
    id_colaborador: '',
    numero_licencia: '',
    operador: '',
    entidad_salud: '',
    tipo_licencia: '',
    fecha_emision: '',
    fecha_inicio: '',
    dias_totales: '',
    fecha_recepcion: '',
    tipo_reposo: 'TOTAL',
    jornada_reposo: '',
    hijo_rut: '',
    hijo_nombre: '',
    hijo_fecha_nacimiento: '',
    fecha_concepcion: '',
    accidente_fecha: '',
    accidente_trayecto: '',
    diat_fecha_envio: '',
    recuperabilidad_laboral: '',
    inicio_tramite_invalidez: '',
    profesional_nombre: '',
    profesional_rut: '',
    profesional_tipo: '',
    establecimiento: '',
    pdf_url: ''
};

const NO_RECEPCION_VACIA = {
    licencia: null,
    motivo: '',
    fecha_termino_relacion: '',
    glosa: '',
    archivo: null,
    documento_url: '',
    tramitacion: null
};

const fechaChilena = (valor) => (valor ? fechaTexto(valor).split('-').reverse().join('-') : '—');
const siNoTexto = (valor) => (valor === true ? 'Sí' : valor === false ? 'No' : '—');
const nombreEn = (catalogo, valor) => (buscar(catalogo, valor) || {}).nombre || '—';
const pesos = (valor) => `$${Math.round(Number(valor) || 0).toLocaleString('es-CL')}`;

/** Valor de un campo booleano de la BD como lo usa el selector del formulario. */
const booleanoAFormulario = (valor) => (valor === true || valor === false ? String(valor) : '');

/**
 * Página de Licencias Médicas
 * Registro de licencias médicas electrónicas y su tramitación en el portal de Fonasa
 */
const LicenciasMedicasPage = ({ token, id_company }) => {
    const [licencias, setLicencias] = useState([]);
    const [colaboradores, setColaboradores] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [modoEdicion, setModoEdicion] = useState(false);
    const [licenciaActual, setLicenciaActual] = useState(null);
    const [extractingPdf, setExtractingPdf] = useState(false);
    const [guardando, setGuardando] = useState(false);
    // Término que trae el PDF (al cargarlo) o el guardado (al editar): la BD lo calcula.
    const [terminoReferencia, setTerminoReferencia] = useState(null);
    const [tramitacion, setTramitacion] = useState(null);
    // PDF leído con IA: se sube recién al guardar la licencia.
    const [archivoPdf, setArchivoPdf] = useState(null);
    const [noRecepcion, setNoRecepcion] = useState(NO_RECEPCION_VACIA);

    // Filtros
    const [filtroEstado, setFiltroEstado] = useState('');

    const [formData, setFormData] = useState(FORMULARIO_VACIO);
    const campo = (nombre) => (e) => setFormData({ ...formData, [nombre]: e.target.value });

    const headers = { Authorization: `Bearer ${token}` };

    // Todos los colaboradores, también los que ya se fueron: una licencia puede llegar
    // después del término y hay que registrarla para no recepcionarla.
    const fetchColaboradores = useCallback(async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/api/rrhh/colaboradores`, {
                params: { id_company },
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.success) {
                setColaboradores(response.data.data);
            }
        } catch (error) {
            console.error('Error al cargar colaboradores:', error);
            Swal.fire('Error', 'No se pudieron cargar los colaboradores', 'error');
        }
    }, [id_company, token]);

    const fetchLicencias = useCallback(async () => {
        try {
            setLoading(true);
            const params = { id_company };
            if (filtroEstado) params.estado_compin = filtroEstado;

            const response = await axios.get(`${API_BASE_URL}/api/rrhh/licencias-medicas`, {
                params,
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                setLicencias(response.data.data);
            }
        } catch (error) {
            console.error('Error al cargar licencias:', error);
            Swal.fire('Error', 'No se pudieron cargar las licencias médicas', 'error');
        } finally {
            setLoading(false);
        }
    }, [id_company, filtroEstado, token]);

    useEffect(() => {
        fetchColaboradores();
    }, [fetchColaboradores]);

    useEffect(() => {
        fetchLicencias();
    }, [fetchLicencias]);

    const fetchTramitacion = async (idLicencia) => {
        const response = await axios.get(
            `${API_BASE_URL}/api/rrhh/licencias-medicas/${idLicencia}/tramitacion`,
            { headers }
        );
        return response.data.data;
    };

    const cerrarModal = () => {
        setShowModal(false);
        setFormData(FORMULARIO_VACIO);
        setModoEdicion(false);
        setLicenciaActual(null);
        setTerminoReferencia(null);
        setTramitacion(null);
        setArchivoPdf(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const errores = validarLicencia(formData);
        if (errores.length) {
            Swal.fire({
                icon: 'warning',
                title: 'Revise la licencia',
                html: `<ul style="text-align:left">${errores.map((err) => `<li>${err}</li>`).join('')}</ul>`
            });
            return;
        }

        try {
            setGuardando(true);
            let response;
            if (modoEdicion) {
                response = await axios.put(
                    `${API_BASE_URL}/api/rrhh/licencias-medicas/${licenciaActual.id}`,
                    { ...formData, id_company },
                    { headers }
                );
            } else {
                // El PDF viaja con los datos y el backend lo sube al crear la licencia:
                // si se cancela el formulario, no queda nada en R2.
                const cuerpo = new FormData();
                Object.keys(formData).forEach((nombre) => cuerpo.append(nombre, formData[nombre] ?? ''));
                cuerpo.append('id_company', id_company);
                if (archivoPdf) cuerpo.append('pdf', archivoPdf);
                response = await axios.post(
                    `${API_BASE_URL}/api/rrhh/licencias-medicas`,
                    cuerpo,
                    { headers: { ...headers, 'Content-Type': 'multipart/form-data' } }
                );
            }

            if (response.data.success) {
                if (response.data.advertencia) {
                    Swal.fire('Licencia registrada', response.data.advertencia, 'warning');
                } else {
                    Swal.fire('Éxito', `Licencia médica ${modoEdicion ? 'actualizada' : 'registrada'} correctamente`, 'success');
                }
                cerrarModal();
                fetchLicencias();
            }
        } catch (error) {
            console.error('Error al guardar licencia:', error);
            Swal.fire('Error', error.response?.data?.message || 'Error al guardar', 'error');
        } finally {
            setGuardando(false);
        }
    };

    const handleEdit = async (licencia) => {
        const datos = {};
        Object.keys(FORMULARIO_VACIO).forEach((nombre) => {
            const valor = licencia[nombre];
            datos[nombre] = valor === null || valor === undefined ? '' : valor;
        });
        ['fecha_emision', 'fecha_inicio', 'fecha_recepcion', 'hijo_fecha_nacimiento', 'fecha_concepcion', 'diat_fecha_envio']
            .forEach((nombre) => { datos[nombre] = licencia[nombre] ? String(licencia[nombre]).split('T')[0] : ''; });
        // Timestamp sin zona: llega en UTC con la hora tal como se guardó.
        datos.accidente_fecha = licencia.accidente_fecha ? String(licencia.accidente_fecha).slice(0, 16) : '';
        ['accidente_trayecto', 'recuperabilidad_laboral', 'inicio_tramite_invalidez']
            .forEach((nombre) => { datos[nombre] = booleanoAFormulario(licencia[nombre]); });

        setModoEdicion(true);
        setLicenciaActual(licencia);
        setFormData(datos);
        setTerminoReferencia({ valor: licencia.fecha_termino, origen: 'guardado' });
        setTramitacion(null);
        setShowModal(true);

        try {
            setTramitacion(await fetchTramitacion(licencia.id));
        } catch (error) {
            console.error('Error al revisar la tramitación:', error);
        }
    };

    const handleCerrar = async (id) => {
        try {
            const result = await Swal.fire({
                title: '¿Cerrar licencia médica?',
                text: 'Esta acción marcará la licencia como cerrada',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Sí, cerrar',
                cancelButtonText: 'Cancelar'
            });

            if (result.isConfirmed) {
                const response = await axios.patch(
                    `${API_BASE_URL}/api/rrhh/licencias-medicas/${id}/cerrar`,
                    {},
                    { headers }
                );

                if (response.data.success) {
                    Swal.fire('Éxito', 'Licencia cerrada correctamente', 'success');
                    fetchLicencias();
                }
            }
        } catch (error) {
            console.error('Error al cerrar licencia:', error);
            Swal.fire('Error', 'No se pudo cerrar la licencia', 'error');
        }
    };

    const handleEliminar = async (id) => {
        try {
            const result = await Swal.fire({
                title: '¿Eliminar licencia médica?',
                text: 'Se eliminará también el PDF de la licencia. Esta acción no se puede deshacer.',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#d33',
                confirmButtonText: 'Sí, eliminar',
                cancelButtonText: 'Cancelar'
            });

            if (result.isConfirmed) {
                const response = await axios.delete(
                    `${API_BASE_URL}/api/rrhh/licencias-medicas/${id}`,
                    { headers }
                );

                if (response.data.success) {
                    Swal.fire('Eliminada', 'La licencia médica fue eliminada', 'success');
                    fetchLicencias();
                }
            }
        } catch (error) {
            console.error('Error al eliminar licencia:', error);
            Swal.fire('Error', 'No se pudo eliminar la licencia', 'error');
        }
    };

    /**
     * Cargar PDF de licencia médica y extraer datos con IA
     */
    const handleExtractFromPdf = async () => {
        const fileInput = document.createElement('input');
        fileInput.type = 'file';
        fileInput.accept = '.pdf';

        fileInput.onchange = async (e) => {
            const file = e.target.files[0];
            if (!file) return;

            if (!file.name.toLowerCase().endsWith('.pdf')) {
                Swal.fire('Error', 'Por favor seleccione un archivo PDF', 'error');
                return;
            }

            setExtractingPdf(true);
            Swal.fire({
                title: 'Procesando PDF...',
                html: 'Extrayendo datos de la licencia médica con IA',
                allowOutsideClick: false,
                didOpen: () => {
                    Swal.showLoading();
                }
            });

            try {
                const formDataUpload = new FormData();
                formDataUpload.append('pdf', file);
                formDataUpload.append('id_company', id_company);

                const response = await axios.post(
                    `${API_BASE_URL}/api/rrhh/licencias-medicas/extract-from-pdf`,
                    formDataUpload,
                    { headers: { ...headers, 'Content-Type': 'multipart/form-data' } }
                );

                if (response.data.success) {
                    const datos = response.data.data;
                    const trabajador = response.data.trabajador || {};

                    // Buscar colaborador por RUT (normalizado para evitar problemas de formato)
                    let colaboradorEncontrado = null;
                    if (trabajador.rut) {
                        const rutBuscado = normalizarRut(trabajador.rut);
                        colaboradorEncontrado = colaboradores.find(
                            col => normalizarRut(col.rut) === rutBuscado
                        );
                    }

                    setFormData({
                        ...FORMULARIO_VACIO,
                        ...datos,
                        accidente_trayecto: booleanoAFormulario(datos.accidente_trayecto),
                        recuperabilidad_laboral: booleanoAFormulario(datos.recuperabilidad_laboral),
                        inicio_tramite_invalidez: booleanoAFormulario(datos.inicio_tramite_invalidez),
                        id_colaborador: colaboradorEncontrado ? colaboradorEncontrado.id : '',
                        // Se propone hoy; corríjala si la licencia llegó otro día.
                        fecha_recepcion: fechaTexto(new Date())
                    });
                    setTerminoReferencia(response.data.fecha_termino_pdf
                        ? { valor: response.data.fecha_termino_pdf, origen: 'pdf' }
                        : null);
                    setTramitacion(null);
                    setArchivoPdf(file);
                    setModoEdicion(false);
                    setLicenciaActual(null);
                    setShowModal(true);

                    const inactivo = colaboradorEncontrado && colaboradorEncontrado.estado !== 'ACTIVO';
                    Swal.fire({
                        icon: 'success',
                        title: 'Datos extraídos',
                        html: `
                            <p><strong>Trabajador:</strong> ${trabajador.nombre || 'No identificado'}</p>
                            <p><strong>RUT:</strong> ${trabajador.rut || 'No identificado'}</p>
                            <p><strong>Folio:</strong> ${datos.numero_licencia || 'N/A'}</p>
                            <p><strong>Tipo:</strong> ${nombreEn(TIPOS_LICENCIA, datos.tipo_licencia)}</p>
                            <p><strong>Días:</strong> ${datos.dias_totales || 'N/A'}</p>
                            ${!colaboradorEncontrado
                                ? '<p style="color: #e74c3c; margin-top: 10px;"><strong>⚠️ Colaborador no encontrado en el sistema. Selecciónelo manualmente.</strong></p>'
                                : '<p style="color: #27ae60; margin-top: 10px;">✓ Colaborador encontrado y seleccionado</p>'}
                            ${inactivo
                                ? `<p style="color: #e67e22;"><strong>El colaborador está ${colaboradorEncontrado.estado}.</strong> Si la licencia empieza después de su salida, guárdela y use "No recepcionar".</p>`
                                : ''}
                        `,
                        confirmButtonText: 'Continuar'
                    });
                }
            } catch (error) {
                console.error('Error al extraer datos del PDF:', error);
                Swal.fire('Error', error.response?.data?.error || error.response?.data?.message || 'No se pudieron extraer los datos del PDF', 'error');
            } finally {
                setExtractingPdf(false);
            }
        };

        fileInput.click();
    };

    /**
     * Descargar ZIP con los documentos para tramitar la licencia: las liquidaciones de
     * los meses que pide el portal y el contrato vigente.
     */
    const handleDescargarDocumentosZip = async (licenciaId) => {
        try {
            Swal.fire({
                title: 'Preparando documentos...',
                html: 'Generando ZIP con liquidaciones y contrato',
                allowOutsideClick: false,
                didOpen: () => {
                    Swal.showLoading();
                }
            });

            const response = await axios.get(
                `${API_BASE_URL}/api/rrhh/licencias-medicas/${licenciaId}/documentos-zip`,
                { headers, responseType: 'blob' }
            );

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;

            const contentDisposition = response.headers['content-disposition'];
            let filename = 'Documentos_Licencia.zip';
            if (contentDisposition) {
                const match = contentDisposition.match(/filename="(.+)"/);
                if (match) filename = match[1];
            }

            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            Swal.fire({
                icon: 'success',
                title: 'Descarga completada',
                text: 'El ZIP incluye las liquidaciones de los meses que pide el portal y el contrato vigente',
                timer: 3000,
                showConfirmButton: false
            });

        } catch (error) {
            console.error('Error al descargar documentos:', error);

            if (error.response?.status === 404) {
                Swal.fire({
                    icon: 'warning',
                    title: 'Sin documentos',
                    text: 'No hay documentos PDF disponibles para esta licencia'
                });
            } else {
                Swal.fire('Error', 'No se pudieron descargar los documentos', 'error');
            }
        }
    };

    // ─── No recepción ─────────────────────────────────────────────────────────

    const handleAbrirNoRecepcion = async (licencia) => {
        setNoRecepcion({ ...NO_RECEPCION_VACIA, licencia });
        try {
            const datos = await fetchTramitacion(licencia.id);
            const sugerencia = datos.sugerencia_no_recepcion;
            setNoRecepcion((prev) => ({
                ...prev,
                tramitacion: datos,
                motivo: sugerencia ? sugerencia.motivo : prev.motivo,
                fecha_termino_relacion: sugerencia ? sugerencia.fecha_termino_relacion : prev.fecha_termino_relacion,
                documento_url: sugerencia && sugerencia.documento_url ? sugerencia.documento_url : prev.documento_url
            }));
        } catch (error) {
            console.error('Error al revisar la licencia:', error);
        }
    };

    const handleGuardarNoRecepcion = async (e) => {
        e.preventDefault();
        const motivo = buscar(MOTIVOS_NO_RECEPCION, noRecepcion.motivo) || {};
        const usaDocumento = !!motivo.pideTermino;
        const errores = validarNoRecepcion({
            motivo: noRecepcion.motivo,
            fecha_termino_relacion: noRecepcion.fecha_termino_relacion,
            glosa: noRecepcion.glosa,
            documento: !!(noRecepcion.archivo || noRecepcion.documento_url)
        });
        if (errores.length) {
            Swal.fire({
                icon: 'warning',
                title: 'Faltan datos',
                html: `<ul style="text-align:left">${errores.map((err) => `<li>${err}</li>`).join('')}</ul>`
            });
            return;
        }

        const cuerpo = new FormData();
        cuerpo.append('motivo', noRecepcion.motivo);
        if (usaDocumento) {
            cuerpo.append('fecha_termino_relacion', noRecepcion.fecha_termino_relacion);
            if (noRecepcion.archivo) cuerpo.append('documento', noRecepcion.archivo);
            else cuerpo.append('documento_url', noRecepcion.documento_url);
        }
        if (motivo.pideGlosa) cuerpo.append('glosa', noRecepcion.glosa);

        try {
            const response = await axios.post(
                `${API_BASE_URL}/api/rrhh/licencias-medicas/${noRecepcion.licencia.id}/no-recepcion`,
                cuerpo,
                { headers: { ...headers, 'Content-Type': 'multipart/form-data' } }
            );
            if (response.data.success) {
                Swal.fire('Listo', 'La licencia quedó registrada como no recepcionada', 'success');
                setNoRecepcion(NO_RECEPCION_VACIA);
                fetchLicencias();
            }
        } catch (error) {
            console.error('Error al no recepcionar:', error);
            Swal.fire('Error', error.response?.data?.message || 'No se pudo registrar la no recepción', 'error');
        }
    };

    const handleDeshacerNoRecepcion = async (licencia) => {
        const result = await Swal.fire({
            title: '¿Deshacer la no recepción?',
            text: 'La licencia vuelve a quedar sin tramitar.',
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Sí, deshacer',
            cancelButtonText: 'Cancelar'
        });
        if (!result.isConfirmed) return;

        try {
            const response = await axios.delete(
                `${API_BASE_URL}/api/rrhh/licencias-medicas/${licencia.id}/no-recepcion`,
                { headers }
            );
            if (response.data.success) {
                Swal.fire('Listo', response.data.message, 'success');
                fetchLicencias();
            }
        } catch (error) {
            console.error('Error al deshacer la no recepción:', error);
            Swal.fire('Error', error.response?.data?.message || 'No se pudo deshacer la no recepción', 'error');
        }
    };

    const copiarDatosPortal = async () => {
        try {
            await navigator.clipboard.writeText(JSON.stringify(tramitacion, null, 2));
            Swal.fire({ icon: 'success', title: 'Copiado', text: 'Los datos para el portal quedaron en el portapapeles', timer: 2000, showConfirmButton: false });
        } catch (error) {
            Swal.fire('Error', 'No se pudo copiar al portapapeles', 'error');
        }
    };

    const tipo = reglasDelTipo(formData.tipo_licencia);
    const motivoNoRecepcion = buscar(MOTIVOS_NO_RECEPCION, noRecepcion.motivo) || {};
    const pagadoraNoRecepcion = noRecepcion.tramitacion && noRecepcion.tramitacion.paso2.entidad_pagadora;

    return (
        <div className="licencias-container">
            <div className="licencias-header">
                <h1><FaFileMedical /> Licencias Médicas</h1>
                <div className="header-buttons">
                    <button
                        className="btn-upload-pdf"
                        onClick={handleExtractFromPdf}
                        disabled={extractingPdf}
                        title="Cargar PDF de licencia médica y extraer datos automáticamente"
                    >
                        <FaUpload /> {extractingPdf ? 'Procesando...' : 'Cargar PDF'}
                    </button>
                </div>
            </div>

            {/* Filtros */}
            <div className="licencias-filters">
                <div className="filter-group">
                    <label>Estado</label>
                    <select value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)}>
                        <option value="">Todos</option>
                        <option value="SIN_TRAMITAR">Sin tramitar</option>
                        <option value="TRAMITADO">Tramitado</option>
                        <option value="NO_RECEPCIONADA">No recepcionadas</option>
                    </select>
                </div>
            </div>

            {/* Tabla de licencias */}
            {loading ? (
                <div className="loading">Cargando licencias médicas...</div>
            ) : (
                <div className="licencias-table-container">
                    <table className="licencias-table">
                        <thead>
                            <tr>
                                <th>RUT</th>
                                <th>Colaborador</th>
                                <th>Tipo</th>
                                <th>Fecha Inicio</th>
                                <th>Fecha Término</th>
                                <th>Días</th>
                                <th>Folio</th>
                                <th>Estado</th>
                                <th>Documentos</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {licencias.length === 0 ? (
                                <tr>
                                    <td colSpan="10" style={{ textAlign: 'center', padding: '30px' }}>
                                        No se encontraron licencias médicas
                                    </td>
                                </tr>
                            ) : (
                                licencias.map((lic) => {
                                    const vencida = lic.vencimiento_tramitacion && fechaTexto(new Date()) > lic.vencimiento_tramitacion;
                                    return (
                                        <tr key={lic.id}>
                                            <td>{lic.rut}</td>
                                            <td>
                                                {lic.nombres} {lic.apellido_paterno}
                                                {lic.estado_colaborador && lic.estado_colaborador !== 'ACTIVO' && (
                                                    <div className="colaborador-inactivo">{lic.estado_colaborador}</div>
                                                )}
                                            </td>
                                            <td>{nombreEn(TIPOS_LICENCIA, lic.tipo_licencia)}</td>
                                            <td>{fechaChilena(lic.fecha_inicio)}</td>
                                            <td>{fechaChilena(lic.fecha_termino)}</td>
                                            <td>{lic.dias_totales}</td>
                                            <td>{lic.numero_licencia || '-'}</td>
                                            <td>
                                                {lic.estado_compin ? (
                                                    <span className={`badge badge-${lic.estado_compin?.toLowerCase()}`}>
                                                        {lic.estado_compin === 'NO_RECEPCIONADA' ? 'NO RECEPCIONADA' : lic.estado_compin}
                                                    </span>
                                                ) : (
                                                    <span className="badge badge-pendiente">SIN TRAMITAR</span>
                                                )}
                                                {lic.vencimiento_tramitacion && (
                                                    <div className={`vence ${vencida ? 'vencida' : ''}`}>
                                                        {vencida ? 'Venció' : 'Vence'} el {fechaChilena(lic.vencimiento_tramitacion)}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="docs-cell">
                                                <button
                                                    className="btn-download-zip"
                                                    onClick={() => handleDescargarDocumentosZip(lic.id)}
                                                    title="Descargar ZIP con las liquidaciones que pide el portal y el contrato"
                                                >
                                                    <FaFileArchive /> Descargar ZIP
                                                </button>
                                            </td>
                                            <td className="actions-cell">
                                                <button className="btn-edit" onClick={() => handleEdit(lic)} title="Editar y revisar datos para el portal">
                                                    <FaEdit />
                                                </button>
                                                {lic.pdf_url && (
                                                    <button
                                                        className="btn-ver-pdf"
                                                        onClick={() => window.open(lic.pdf_url, '_blank')}
                                                        title="Ver PDF de la licencia"
                                                    >
                                                        <FaFilePdf />
                                                    </button>
                                                )}
                                                {!lic.estado_compin && (
                                                    <button
                                                        className="btn-no-recepcionar"
                                                        onClick={() => handleAbrirNoRecepcion(lic)}
                                                        title="No recepcionar la licencia"
                                                    >
                                                        <FaBan />
                                                    </button>
                                                )}
                                                {lic.estado_compin === 'NO_RECEPCIONADA' && (
                                                    <button
                                                        className="btn-deshacer"
                                                        onClick={() => handleDeshacerNoRecepcion(lic)}
                                                        title="Deshacer la no recepción"
                                                    >
                                                        <FaUndo />
                                                    </button>
                                                )}
                                                {lic.estado_compin !== 'TRAMITADO' && lic.estado_compin !== 'NO_RECEPCIONADA' && (
                                                    <button
                                                        className="btn-cerrar"
                                                        onClick={() => handleCerrar(lic.id)}
                                                        title="Cerrar licencia"
                                                    >
                                                        <FaCheckCircle />
                                                    </button>
                                                )}
                                                <button
                                                    className="btn-eliminar"
                                                    onClick={() => handleEliminar(lic.id)}
                                                    title="Eliminar licencia"
                                                >
                                                    <FaTrash />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Modal de formulario */}
            {showModal && (
                <div className="modal-overlay" onClick={cerrarModal}>
                    <div className="modal-content licencias-modal" onClick={(e) => e.stopPropagation()}>
                        <h2>{modoEdicion ? 'Editar Licencia Médica' : 'Nueva Licencia Médica'}</h2>
                        {!modoEdicion && archivoPdf && (
                            <p className="form-hint">PDF: {archivoPdf.name}. Se guarda junto con la licencia.</p>
                        )}
                        <form onSubmit={handleSubmit}>
                            <div className="form-section">
                                <h3>Licencia</h3>
                                <div className="form-grid">
                                    <div className="form-group">
                                        <label>Colaborador *</label>
                                        <select
                                            value={formData.id_colaborador}
                                            onChange={campo('id_colaborador')}
                                            required
                                            disabled={modoEdicion}
                                        >
                                            <option value="">Seleccione...</option>
                                            {colaboradores.map(col => (
                                                <option key={col.id} value={col.id}>
                                                    {col.nombres} {col.apellido_paterno}{col.estado !== 'ACTIVO' ? ` (${col.estado})` : ''}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label>Folio *</label>
                                        <input
                                            type="text"
                                            value={formData.numero_licencia}
                                            onChange={campo('numero_licencia')}
                                            required
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Tipo de Licencia *</label>
                                        <select
                                            value={formData.tipo_licencia}
                                            onChange={campo('tipo_licencia')}
                                            required
                                        >
                                            <option value="">Seleccione...</option>
                                            {TIPOS_LICENCIA.map(t => (
                                                <option key={t.valor} value={t.valor}>
                                                    {t.codigo ? `${t.codigo} - ` : ''}{t.nombre}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label>Fecha Emisión *</label>
                                        <input type="date" value={formData.fecha_emision} onChange={campo('fecha_emision')} required />
                                    </div>
                                    <div className="form-group">
                                        <label>Inicio del Reposo *</label>
                                        <input type="date" value={formData.fecha_inicio} onChange={campo('fecha_inicio')} required />
                                    </div>
                                    <div className="form-group">
                                        <label>Días de Reposo *</label>
                                        <input
                                            type="number"
                                            value={formData.dias_totales}
                                            onChange={campo('dias_totales')}
                                            required
                                            min="1"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Término del Reposo</label>
                                        <div className="campo-lectura">
                                            {terminoReferencia ? fechaChilena(terminoReferencia.valor) : '—'}
                                        </div>
                                        <span className="form-hint">
                                            {terminoReferencia && terminoReferencia.origen === 'pdf'
                                                ? 'Según el PDF. Al guardar se calcula: inicio + días − 1.'
                                                : 'Se calcula al guardar: inicio + días − 1.'}
                                        </span>
                                    </div>
                                    <div className="form-group">
                                        <label>Tipo de Reposo *</label>
                                        <div className="radio-group">
                                            {['TOTAL', 'PARCIAL'].map(valor => (
                                                <label key={valor} className="radio-label">
                                                    <input
                                                        type="radio"
                                                        name="tipo_reposo"
                                                        value={valor}
                                                        checked={formData.tipo_reposo === valor}
                                                        onChange={campo('tipo_reposo')}
                                                        disabled={valor === 'PARCIAL' && !!tipo.maternal}
                                                    />
                                                    {valor === 'TOTAL' ? 'Total' : 'Parcial'}
                                                </label>
                                            ))}
                                        </div>
                                    </div>
                                    {formData.tipo_reposo === 'PARCIAL' && (
                                        <div className="form-group">
                                            <label>Jornada del Reposo *</label>
                                            <select value={formData.jornada_reposo} onChange={campo('jornada_reposo')} required>
                                                <option value="">Seleccione...</option>
                                                {JORNADAS_REPOSO.map(j => (
                                                    <option key={j.valor} value={j.valor}>{j.nombre}</option>
                                                ))}
                                            </select>
                                        </div>
                                    )}
                                </div>
                                {tipo.destino === 'MUTUAL' && (
                                    <p className="aviso-tipo">Se tramita con la mutual o el ISL, junto con la DIAT (accidente) o la DIEP (enfermedad profesional).</p>
                                )}
                                {tipo.destino === 'COMPIN' && (
                                    <p className="aviso-tipo">La licencia SANNA se presenta en la COMPIN con su formulario propio y copia del contrato.</p>
                                )}
                            </div>

                            {(tipo.requiereHijo || tipo.admiteConcepcion) && (
                                <div className="form-section">
                                    <h3>{tipo.requiereHijo ? 'Hijo o hija' : 'Embarazo'}</h3>
                                    <div className="form-grid">
                                        {tipo.requiereHijo && (
                                            <>
                                                <div className="form-group">
                                                    <label>RUT *</label>
                                                    <input type="text" value={formData.hijo_rut} onChange={campo('hijo_rut')} required />
                                                </div>
                                                <div className="form-group">
                                                    <label>Nombre</label>
                                                    <input type="text" value={formData.hijo_nombre} onChange={campo('hijo_nombre')} />
                                                </div>
                                                <div className="form-group">
                                                    <label>Fecha de Nacimiento *</label>
                                                    <input type="date" value={formData.hijo_fecha_nacimiento} onChange={campo('hijo_fecha_nacimiento')} required />
                                                </div>
                                            </>
                                        )}
                                        {tipo.admiteConcepcion && (
                                            <div className="form-group">
                                                <label>Fecha de Concepción</label>
                                                <input type="date" value={formData.fecha_concepcion} onChange={campo('fecha_concepcion')} />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {tipo.leyAccidentes && (
                                <div className="form-section">
                                    <h3>{tipo.requiereAccidente ? 'Accidente' : 'Enfermedad profesional'}</h3>
                                    <div className="form-grid">
                                        {tipo.requiereAccidente && (
                                            <>
                                                <div className="form-group">
                                                    <label>Fecha y Hora del Accidente *</label>
                                                    <input type="datetime-local" value={formData.accidente_fecha} onChange={campo('accidente_fecha')} required />
                                                </div>
                                                <div className="form-group">
                                                    <label>¿De Trayecto? *</label>
                                                    <select value={formData.accidente_trayecto} onChange={campo('accidente_trayecto')} required>
                                                        <option value="">Seleccione...</option>
                                                        <option value="true">Sí</option>
                                                        <option value="false">No</option>
                                                    </select>
                                                </div>
                                            </>
                                        )}
                                        <div className="form-group">
                                            <label>Envío de la {tipo.requiereAccidente ? 'DIAT' : 'DIEP'}</label>
                                            <input type="date" value={formData.diat_fecha_envio} onChange={campo('diat_fecha_envio')} />
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="form-section">
                                <h3>Tramitación</h3>
                                <div className="form-grid">
                                    <div className="form-group">
                                        <label>Fecha de Recepción *</label>
                                        <input type="date" value={formData.fecha_recepcion} onChange={campo('fecha_recepcion')} required />
                                        <span className="form-hint">Día en que la empresa recibió la licencia. Desde ahí corren 3 días hábiles para tramitarla.</span>
                                    </div>
                                    <div className="form-group">
                                        <label>Operador</label>
                                        <div className="campo-lectura">{nombreEn(OPERADORES, formData.operador)}</div>
                                    </div>
                                    <div className="form-group">
                                        <label>Entidad que se Pronuncia</label>
                                        <div className="campo-lectura">{formData.entidad_salud || '—'}</div>
                                    </div>
                                </div>
                            </div>

                            <div className="form-section">
                                <h3>Profesional (según el PDF)</h3>
                                <div className="form-grid">
                                    <div className="form-group">
                                        <label>Nombre</label>
                                        <div className="campo-lectura">{formData.profesional_nombre || '—'}</div>
                                    </div>
                                    <div className="form-group">
                                        <label>RUT</label>
                                        <div className="campo-lectura">{formData.profesional_rut || '—'}</div>
                                    </div>
                                    <div className="form-group">
                                        <label>Tipo de Profesional</label>
                                        <div className="campo-lectura">{nombreEn(TIPOS_PROFESIONAL, formData.profesional_tipo)}</div>
                                    </div>
                                    <div className="form-group">
                                        <label>Establecimiento</label>
                                        <div className="campo-lectura">{formData.establecimiento || '—'}</div>
                                    </div>
                                    <div className="form-group">
                                        <label>Recuperabilidad Laboral</label>
                                        <div className="campo-lectura">{siNoTexto(formData.recuperabilidad_laboral === '' ? null : formData.recuperabilidad_laboral === 'true')}</div>
                                    </div>
                                    <div className="form-group">
                                        <label>Inicio Trámite de Invalidez</label>
                                        <div className="campo-lectura">{siNoTexto(formData.inicio_tramite_invalidez === '' ? null : formData.inicio_tramite_invalidez === 'true')}</div>
                                    </div>
                                </div>
                            </div>

                            {modoEdicion && (
                                <div className="form-section revision-panel">
                                    <h3>Revisión para el portal (PIEE)</h3>
                                    {!tramitacion ? (
                                        <p>Revisando...</p>
                                    ) : (
                                        <>
                                            {tramitacion.accion === 'NO_RECEPCIONAR' && (
                                                <p className="revision-ok">No recepcionada: {tramitacion.no_recepcion?.motivo?.nombre}. Lista para registrarla en el portal.</p>
                                            )}
                                            {tramitacion.accion === 'NINGUNA' && (
                                                <p>Esta licencia ya está {String(licenciaActual?.estado_compin || '').toLowerCase()}.</p>
                                            )}
                                            {tramitacion.accion === 'TRAMITAR' && (
                                                tramitacion.listo
                                                    ? <p className="revision-ok">Lista para tramitar en el portal.</p>
                                                    : tramitacion.faltantes.length > 0 && (
                                                        <div className="revision-faltan">
                                                            <strong>Falta para tramitarla:</strong>
                                                            <ul>{tramitacion.faltantes.map((f) => <li key={f}>{f}</li>)}</ul>
                                                        </div>
                                                    )
                                            )}
                                            {tramitacion.avisos.length > 0 && (
                                                <ul className="revision-avisos">
                                                    {tramitacion.avisos.map((a) => <li key={a}>{a}</li>)}
                                                </ul>
                                            )}
                                            <div className="revision-datos">
                                                <div><span>Vence el</span> {fechaChilena(tramitacion.plazo.vencimiento)}{tramitacion.plazo.vencida ? ' (vencido)' : ''}</div>
                                                <div><span>Entidad pagadora</span> {tramitacion.paso2.entidad_pagadora ? `${tramitacion.paso2.entidad_pagadora.tipo} · ${tramitacion.paso2.entidad_pagadora.nombre}` : '—'}</div>
                                                <div><span>Ocupación</span> {tramitacion.paso1.ocupacion ? tramitacion.paso1.ocupacion.nombre : '—'}</div>
                                                <div><span>Licencias anteriores (6 meses)</span> {tramitacion.paso4.licencias_anteriores.length}</div>
                                                {tramitacion.licencia.continuacion_de_folio && (
                                                    <div><span>Continuación de</span> folio {tramitacion.licencia.continuacion_de_folio}</div>
                                                )}
                                                <div><span>Documentos</span> {tramitacion.documentos.length}</div>
                                            </div>
                                            {tramitacion.paso3.rentas.concat(tramitacion.paso3.rentas_maternales).length > 0 && (
                                                <table className="periodos-table">
                                                    <thead>
                                                        <tr><th>Mes</th><th>Días</th><th>Total remuneración</th><th>Días incapacidad</th></tr>
                                                    </thead>
                                                    <tbody>
                                                        {tramitacion.paso3.rentas.concat(tramitacion.paso3.rentas_maternales).map((r) => (
                                                            <tr key={r.periodo}>
                                                                <td>{r.etiqueta}</td>
                                                                <td>{r.dias}</td>
                                                                <td>{pesos(r.total_remuneracion)}</td>
                                                                <td>{r.dias_incapacidad}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            )}
                                            <button type="button" className="btn-copiar" onClick={copiarDatosPortal}>
                                                Copiar datos para el portal
                                            </button>
                                        </>
                                    )}
                                </div>
                            )}

                            <div className="modal-actions">
                                <button type="button" className="btn-cancel" onClick={cerrarModal}>
                                    Cancelar
                                </button>
                                <button type="submit" className="btn-save" disabled={guardando}>
                                    {guardando ? 'Guardando...' : modoEdicion ? 'Actualizar' : 'Guardar'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Modal de no recepción */}
            {noRecepcion.licencia && (
                <div className="modal-overlay" onClick={() => setNoRecepcion(NO_RECEPCION_VACIA)}>
                    <div className="modal-content no-recepcion-modal" onClick={(e) => e.stopPropagation()}>
                        <h2>No recepcionar licencia</h2>
                        <p className="form-hint">
                            Folio {noRecepcion.licencia.numero_licencia} · {noRecepcion.licencia.nombres} {noRecepcion.licencia.apellido_paterno} ·
                            desde el {fechaChilena(noRecepcion.licencia.fecha_inicio)} por {noRecepcion.licencia.dias_totales} días
                        </p>
                        <form onSubmit={handleGuardarNoRecepcion}>
                            <div className="form-section">
                                <h3>Motivo</h3>
                                <div className="radio-group vertical">
                                    {MOTIVOS_NO_RECEPCION.map((m) => (
                                        <label key={m.valor} className="radio-label">
                                            <input
                                                type="radio"
                                                name="motivo"
                                                value={m.valor}
                                                checked={noRecepcion.motivo === m.valor}
                                                onChange={(e) => setNoRecepcion({ ...noRecepcion, motivo: e.target.value })}
                                            />
                                            {m.nombre}
                                        </label>
                                    ))}
                                </div>
                            </div>

                            {motivoNoRecepcion.pideTermino && (
                                <div className="form-section">
                                    <h3>Término de la relación laboral</h3>
                                    <div className="form-grid">
                                        <div className="form-group">
                                            <label>Fecha de Término *</label>
                                            <input
                                                type="date"
                                                value={noRecepcion.fecha_termino_relacion}
                                                onChange={(e) => setNoRecepcion({ ...noRecepcion, fecha_termino_relacion: e.target.value })}
                                                required
                                            />
                                        </div>
                                        <div className="form-group full-width">
                                            <label>Documento que lo acredita *</label>
                                            {noRecepcion.documento_url && !noRecepcion.archivo && (
                                                <span className="form-hint">
                                                    Se usará el <a href={noRecepcion.documento_url} target="_blank" rel="noopener noreferrer">PDF del finiquito</a>. Para usar otro, adjúntelo.
                                                </span>
                                            )}
                                            <input
                                                type="file"
                                                accept=".pdf"
                                                onChange={(e) => setNoRecepcion({ ...noRecepcion, archivo: e.target.files[0] || null })}
                                            />
                                        </div>
                                    </div>
                                    {pagadoraNoRecepcion && ['CCAF', 'COMPIN'].includes(pagadoraNoRecepcion.tipo) && (
                                        <p className="form-hint">
                                            Licencia de Fonasa: el portal pide la caja de compensación con convenio. Se informará {pagadoraNoRecepcion.tipo === 'CCAF' ? pagadoraNoRecepcion.nombre : 'que la empresa no tiene caja'}.
                                        </p>
                                    )}
                                </div>
                            )}

                            {motivoNoRecepcion.pideGlosa && (
                                <div className="form-section">
                                    <h3>Motivo por el cual no se tramitará *</h3>
                                    <div className="form-group">
                                        <textarea
                                            rows="3"
                                            value={noRecepcion.glosa}
                                            onChange={(e) => setNoRecepcion({ ...noRecepcion, glosa: e.target.value })}
                                            required
                                        />
                                    </div>
                                </div>
                            )}

                            <div className="modal-actions">
                                <button type="button" className="btn-cancel" onClick={() => setNoRecepcion(NO_RECEPCION_VACIA)}>
                                    Cancelar
                                </button>
                                <button type="submit" className="btn-save">No recepcionar</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LicenciasMedicasPage;
